/****************************************************
 * Module Name:  mixCustomerWisePendingPayment.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import React, { useEffect, useState } from "react";
import Constants from "../../utility/constants";
import { Box, Button, CircularProgress, Tooltip, Stack, Typography } from "@mui/material";
import { DataGridPro, GridToolbarExport, GridToolbarColumnsButton, GridToolbarContainer, GridToolbarFilterButton, GridToolbarQuickFilter } from "@mui/x-data-grid-pro";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import CommonSnackbar from "../common/commonSnackBar";
import renderCellExpand from "../common/dataGridCellExpand";
import Utility from "../../utility/utility";
import { Modal } from "react-bootstrap";
import logo from '../../images/pansuriya.jpg'
import MixSaleProxy from "../../utility/proxy/manager/mixProxy/mixSalesProxy";
import MixSellInvoice from "./mixSellInvoice";
import NoPermission from "../common/noPermission";
import PageTitle from "../common/pageTitle";

const MixCustomerWisePendingPayment = () => {

    const localPermission = Constants.USER_PERMISSION.filter((iPermission) =>
        iPermission.permissionid === Constants.PERMISSIONS.MIX_REPORT_CUSTOMER_WISE_PENDING_PAYMENT);

    const [listData, setListData] = useState([]);
    const [loader, setLoader] = useState(false);
    const [filterValues, setFilterValues] = useState({});
    const [invoiceDetailModal, setInvoiceDetailModal] = useState(false);

    const [showSnackbar, setShowSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarVariant, setSnackbarVariant] = useState("");

    const [totalPaid, setTotalPaid] = useState(0);
    const [totalAmount, setTotalAmount] = useState(0);
    const [totalPending, setTotalPending] = useState(0);
    const [totalPaidInr, setTotalPaidInr] = useState(0);
    const [totalAmountInr, setTotalAmountInr] = useState(0);
    const [totalPendingInr, setTotalPendingInr] = useState(0);

    const [customerData, setCustomerData] = useState({
        customer: [],
        customerOptions: [],
    });
    const defaultStatus = [{ value: -1, label: "-- ANY --" }];

    useEffect(() => {
        PageTitle("MIX_REPORT_CUSTOMER_WISE_PENDING_PAYMENT");
        handleSearch();
    }, []);

    const handleSearch = () => {
        setLoader(true)
        let userid = Utility.isReadAllowed(localPermission) ? -1 : Constants.loggedInUser.id;
        MixSaleProxy.GetAllMixCustomerWisePendingPayment(userid, Constants.loggedInUser.usertype, (response) => {
            if (response) {
                const transformedData = response.map(item => ({
                    ...item,
                    usertype1: Constants.USER_TYPE[item.usertype]
                }));

                const uniqueFilterValues = {
                    designation: Utility.getDistinctOptions(transformedData, "designation"),
                    usertype: Utility.getDistinctOptions(transformedData, "usertype1"),
                    grade: Utility.getDistinctOptions(transformedData, "grade"),
                    location: Utility.getDistinctOptions(transformedData, "location"),
                    locationcountry: Utility.getDistinctOptions(transformedData, "locationcountry"),
                    city: Utility.getDistinctOptions(transformedData, "city"),
                    state: Utility.getDistinctOptions(transformedData, "state"),
                    country: Utility.getDistinctOptions(transformedData, "country"),
                    title: Utility.getDistinctOptions(transformedData, "title"),
                    companyname: Utility.getDistinctOptions(transformedData, "companyname"),
                }

                setFilterValues(uniqueFilterValues);
                setListData(transformedData.sort((a, b) => b.totalInvoice - a.totalInvoice));
                getSummaryReport(transformedData);
                setLoader(false);
            } else {
                setLoader(false);
                setShowSnackbar(true);
                setSnackbarMessage(Constants.APiFailErrorMessage);
                setSnackbarVariant("error");
            }
        });
    };

    const getSummaryReport = (response) => {
        let totalAmount = 0, totalPaid = 0, totalPending = 0;
        let totalAmountINR = 0, totalPaidINR = 0, totalPendingINR = 0;
        response.forEach(iData => {
            totalAmount += iData.totalamount;
            totalPaid += iData.paidamount;
            totalPending += iData.pendingamount;
            totalAmountINR += iData.totalamountinr;
            totalPaidINR += iData.paidamountinr;
            totalPendingINR += iData.pendingamountinr;
        });

        setTotalAmount(totalAmount);
        setTotalPaid(totalPaid);
        setTotalPending(totalPending);
        setTotalAmountInr(totalAmountINR);
        setTotalPaidInr(totalPaidINR);
        setTotalPendingInr(totalPendingINR);
    }

    const handleClick = (params) => {
        setCustomerData({
            customerOptions: [{ value: params.row.id, label: params.row.companyname }],
            customer: [{ value: params.row.id, label: params.row.companyname }],
        })
        setInvoiceDetailModal(true)
    }

    const columnName = [
        {
            field: "id",
            headerName: "ID",
            headerAlign: "center",
            align: "center",
            width: 100,
        },
        {
            field: "username",
            headerName: "USER",
            headerAlign: "center",
            align: "center",
            width: 180,
            renderCell: renderCellExpand,
        },
        {
            field: "companyname",
            headerName: "COMPANY NAME",
            headerAlign: "center",
            align: "center",
            width: 180,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.companyname : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            cellClassName: 'lavenderCell',
        },
        {
            field: "first_name",
            headerName: "FIRST NAME",
            headerAlign: "center",
            align: "center",
            width: 130,
            renderCell: renderCellExpand,
            cellClassName: 'lavenderCell',
        },
        {
            field: "middle_name",
            headerName: "MIDDLE NAME",
            headerAlign: "center",
            align: "center",
            width: 130,
            renderCell: renderCellExpand,
            cellClassName: 'lavenderCell',
        },
        {
            field: "last_name",
            headerName: "LAST NAME",
            headerAlign: "center",
            align: "center",
            width: 120,
            renderCell: renderCellExpand,
            cellClassName: 'lavenderCell',
        },
        {
            field: "designation",
            headerName: "DESIGNATION",
            headerAlign: "center",
            align: "center",
            width: 100,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.designation : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            disableExport: true
        },
        {
            field: "usertype1",
            headerName: "TYPE",
            headerAlign: "center",
            align: "center",
            width: 90,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.usertype : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            disableExport: true
        },
        {
            field: "employeeid",
            headerName: "EMPLOYEE ID",
            headerAlign: "center",
            align: "center",
            width: 100,
            renderCell: renderCellExpand,
        },
        {
            field: "locationid",
            headerName: "LOCATION ID",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            width: 50,
            renderCell: renderCellExpand,
        },
        {
            field: "location",
            headerName: "LOCATION",
            headerAlign: "center",
            align: "center",
            width: 100,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.location : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "country",
            headerName: "COUNTRY",
            headerAlign: "center",
            align: "center",
            width: 100,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.country : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "discount",
            headerName: "DIS",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            width: 50,
        },
        {
            field: "gstinno",
            headerName: "GSTIN No",
            headerAlign: "center",
            align: "center",
            width: 100,
        },
        {
            field: "panno",
            headerName: "PAN No",
            headerAlign: "center",
            align: "center",
            width: 100,
        },
        {
            field: "totalamount",
            headerName: "TOTAL AMOUNT",
            headerAlign: "center",
            align: "center",
            width: 110,
            type: "number",
            renderCell: (params) => params.value ? (params.value).toFixed(2) : 0.00
        },
        {
            field: "paidamount",
            headerName: "PAID AMOUNT",
            headerAlign: "center",
            align: "center",
            width: 100,
            type: "number",
            renderCell: (params) => params.value ? (params.value).toFixed(2) : 0.00
        },
        {
            field: "pendingamount",
            headerName: "PENDING AMOUNT",
            headerAlign: "center",
            align: "center",
            width: 130,
            type: "number",
            renderCell: (params) => params.value ? (params.value).toFixed(2) : 0.00
        },
        {
            field: "totalamountinr",
            headerName: "TOTAL AMOUNT INR",
            headerAlign: "center",
            align: "center",
            width: 135,
            type: "number",
            renderCell: (params) => params.value ? (params.value).toFixed(2) : 0.00
        },
        {
            field: "paidamountinr",
            headerName: "PAID AMOUNT INR",
            headerAlign: "center",
            align: "center",
            width: 125,
            type: "number",
            renderCell: (params) => params.value ? (params.value).toFixed(2) : 0.00
        },
        {
            field: "pendingamountinr",
            headerName: "PENDING AMOUNT INR",
            headerAlign: "center",
            align: "center",
            width: 150,
            type: "number",
            renderCell: (params) => params.value ? (params.value).toFixed(2) : 0.00
        },
        {
            field: "totalInvoice",
            headerName: "TOTAL INVOICE",
            headerAlign: "center",
            align: "center",
            width: 110,
            type: "number",
            renderCell: (params) => {
                return (
                    <Button
                        sx={{
                            textDecorationLine: "underline",
                            color: 'black'
                        }}
                        onClick={() => handleClick(params)}
                    >
                        {params.value}
                    </Button>
                )
            }
        },
    ];

    const columns = columnName;

    const CustomToolbar = () => {
        return (
            <GridToolbarContainer
                sx={{
                    display: "flex",
                    alignItems: "center",
                    marginHorizontal: "5px",
                    "&.MuiDataGrid-toolbarContainer": {
                        padding: "0px 4px !important",
                    },
                }}
            >
                <GridToolbarColumnsButton />
                <GridToolbarFilterButton />
                <GridToolbarExport />
                <Box sx={{ flexGrow: 1, display: { md: "none", lg: "block" } }} />
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        flexGrow: 1,
                        minWidth: 0,
                        px: 1,
                    }}
                >
                    <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={2}
                        useFlexGap
                        flexWrap="wrap"
                        sx={{
                            width: "100%",
                            alignItems: { xs: "flex-start", sm: "center" },
                            justifyContent: { xs: "flex-start", sm: "flex-start" },
                        }}
                    >
                        <Typography component="span" sx={{ fontSize: Constants.defaultTableFontSize }}>
                            Total Amount <b>{totalAmount.toFixed(2)}</b>
                        </Typography>

                        <Typography component="span" sx={{ fontSize: Constants.defaultTableFontSize }}>
                            Total Paid <b>{totalPaid.toFixed(2)}</b>
                        </Typography>
                        <Typography component="span" sx={{ fontSize: Constants.defaultTableFontSize }}>
                            Total Pending <b>{totalPending.toFixed(2)}</b>
                        </Typography>

                        <Typography component="span" sx={{ fontSize: Constants.defaultTableFontSize }}>
                            Total Amount INR <b>{totalAmountInr.toFixed(2)}</b>
                        </Typography>
                        <Typography component="span" sx={{ fontSize: Constants.defaultTableFontSize }}>
                            Total Paid INR <b>{totalPaidInr.toFixed(2)}</b>
                        </Typography>
                        <Typography component="span" sx={{ fontSize: Constants.defaultTableFontSize }}>
                            Total Pending INR <b>{totalPendingInr.toFixed(2)}</b>
                        </Typography>
                    </Stack>
                </Box>

                {loader ? (
                    <CircularProgress size="1rem" />
                ) : (
                    <Tooltip title="Reload List">
                        <RefreshOutlinedIcon
                            onClick={() =>
                                handleSearch()
                            }
                            color="primary"
                            fontSize="medium"
                            sx={{ cursor: "pointer" }}
                        />
                    </Tooltip>
                )}
                <GridToolbarQuickFilter debounceMs={500} />
            </GridToolbarContainer>
        );
    }

    return (

        <Box>
            {Utility.isReadAllowed(localPermission) || Constants.loggedInUser.usertype !== "B" ?
                <Box>

                    <CommonSnackbar
                        show={showSnackbar}
                        message={snackbarMessage}
                        variant={snackbarVariant}
                        autoHide={Constants.SnackBarCloseTimer}
                        title={snackbarVariant === "success" ? "Success" : snackbarVariant === "error" ? "Error" : "Warning"}
                        handleClose={() => setShowSnackbar(false)}
                    />

                    <Box
                        sx={{
                            backgroundColor: '#fff',
                            color: '#000',
                            marginTop: "16px",
                            width: "100%",
                            position: "relative",
                            "& .MuiTablePagination-selectLabel": {
                                marginTop: "15px",
                            },
                            "& .MuiTablePagination-displayedRows": {
                                marginTop: "15px",
                            },
                            "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                                fontWeight: Constants.defaultFontWeight,
                            },
                            "& .css-cc8tf1": {
                                fontWeight: Constants.defaultFontWeight,
                            },
                            height: "84vh",
                        }}
                    >
                        <DataGridPro
                            disableRowSelectionOnClick={true}
                            sx={{
                                '& .MuiDataGrid-columnHeader': {
                                    color: '#fff',
                                    backgroundColor: 'grey',
                                },
                                "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                                    py: "4px",
                                },
                                "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                                    py: "12px",
                                },
                                "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                                    py: "18px",
                                },

                                fontSize: Constants.defaultTableFontSize, fontWeight: Constants.defaultFontWeight,
                            }}
                            density="compact"
                            slots={{ toolbar: CustomToolbar }}
                            rows={listData}
                            columns={columns}
                            loading={loader}
                            onRowDoubleClick={handleClick}
                            getRowId={(row) => row.id}
                            initialState={{
                                columns: {
                                    columnVisibilityModel: {
                                        // Hide columns id, the other columns will remain visible
                                        locationid: false,
                                        employeeid: false,
                                        id: false,
                                        middle_name: false,
                                        country: false,
                                        usertype1: false,
                                        designation: false
                                    },
                                },
                                pagination: {
                                    paginationModel: Constants.pageSize,
                                },
                            }}
                            pageSizeOptions={Constants.pageSizeOptions}
                            pagination
                            slotProps={{
                                toolbar: {
                                    showQuickFilter: true,
                                    quickFilterProps: { debounceMs: 500 },
                                },
                            }}
                        />

                    </Box>

                    {invoiceDetailModal ? (
                        <Modal
                            enforceFocus={false}
                            show={true}
                            onHide={() => setInvoiceDetailModal(false)}
                            backdrop="static"
                            dialogClassName="max-modalSize-Tab"
                        >
                            <Modal.Header closeButton>
                                <Modal.Title>
                                    <img
                                        src={logo}
                                        height="40pt"
                                        width="40pt"
                                        alt={Constants.COMPANY_SITE}
                                    />
                                    &nbsp;&nbsp;
                                    Invoice Details
                                </Modal.Title>
                            </Modal.Header>
                            <Modal.Body>
                                <MixSellInvoice
                                    loadedCustomers={customerData}
                                    defaultfromdate={null}
                                    defaulttodate={null}
                                    defaultStatus={defaultStatus}
                                    fromOtherComponent={true}
                                />
                            </Modal.Body>
                            <Modal.Footer>
                                <Button variant="contained" color="error" onClick={() => setInvoiceDetailModal(false)}>Close</Button>
                            </Modal.Footer>
                        </Modal>
                    ) : null}

                </Box> : <NoPermission />}
        </Box>

    );
};

export default MixCustomerWisePendingPayment;